import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {Currency} from "@pg-rp/common-types";

import {replaceDigitsWithZeros} from "../../../credit_calculator/utils/replace_digits_with_zeros";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {getCurrency} from "../../../utils/get_currency";
import {PropertyCarouselSize} from "../../types/PropertyCarouselSize";

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

interface IProps {
    href: string;
    imageSrc?: string;
    investmentName: string;
    rooms: number;
    area: number;
    floor: number | null;
    price: number | null;
    currency?: Currency;
    location: string;
    size?: PropertyCarouselSize;
    onClick?: () => void;
    dataTestId?: string;
}

export const PropertyCarouselTile = (props: IProps) => {
    const {href, imageSrc, investmentName, rooms, area, floor, price, currency, location, size = "small", onClick, dataTestId} = props;
    const {shouldHidePrices} = useShouldHidePrices();

    const hasPrice = price !== null && price > 0;
    const priceText = hasPrice ? priceFormat(price, {unit: getCurrency({currency})}) || null : null;
    const displayPrice = shouldHidePrices && priceText ? replaceDigitsWithZeros(priceText) : priceText;
    const floorLabel = getFloorLabel(floor);

    return (
        <li className={listItemStyle[size]} data-testid={dataTestId}>
            <article className={cardStyle}>
                <a
                    className={cardLinkStyle}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Zobacz ofertę: ${investmentName}`}
                    onClick={onClick}
                >
                    <div className={imageWrapperStyle[size]}>
                        <Badge variant="label_default" className={investmentBadgeStyle[size]}>
                            <Text variant="info_txt_2" as="span" strong>
                                {investmentName}
                            </Text>
                        </Badge>
                        <img className={imageStyle} src={imageSrc} alt={investmentName} loading="lazy" />
                    </div>

                    <div className={contentStyle[size]}>
                        <Text variant="info_txt_1" as="div" className={titleStyle[size]}>
                            {rooms} {roomsPluralize(rooms)}, {areaFormat(area)}
                            {floorLabel ? `, ${floorLabel}` : ""}
                        </Text>

                        {displayPrice && (
                            <Text
                                variant="info_txt_1"
                                as="div"
                                preserveFontSize={size === "large"}
                                className={cx(priceStyle, shouldHidePrices && blurredPriceStyle)}
                                aria-hidden={shouldHidePrices}
                            >
                                <Highlight strong>{displayPrice}</Highlight>
                            </Text>
                        )}

                        {location && (
                            <Text variant="info_txt_3" as="div" className={locationStyle}>
                                {location}
                            </Text>
                        )}
                    </div>
                </a>
            </article>
        </li>
    );
};

function getFloorLabel(floor: number | null): string {
    if (floor === null) {
        return "";
    }

    if (floor === 0) {
        return "parter";
    }

    return `${floor} piętro`;
}

const listItemStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        flex: "0 0 156px",
        scrollSnapAlign: "start",
        minWidth: "156px"
    }),
    large: css({
        flex: "0 0 192px",
        scrollSnapAlign: "start",
        minWidth: "192px"
    })
};

const cardStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "var(--colors-white)",
    borderRadius: 1,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden"
});

const cardLinkStyle = css({
    display: "flex",
    flexDirection: "column",
    color: "inherit",
    textDecoration: "none",
    minHeight: "100%",

    _hover: {
        color: "inherit",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "-2px"
    }
});

const imageWrapperStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        position: "relative",
        width: "100%",
        aspectRatio: "154/128",
        backgroundColor: "var(--colors-gray-100)",
        overflow: "hidden"
    }),
    large: css({
        position: "relative",
        width: "100%",
        aspectRatio: "192/157",
        backgroundColor: "var(--colors-gray-100)",
        overflow: "hidden"
    })
};

const investmentBadgeStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        position: "absolute",
        top: 0.5,
        left: 0.5,
        zIndex: 1,
        maxWidth: "calc(100% - var(--spacing-1))",
        py: 0.5,
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    }),
    large: css({
        position: "absolute",
        top: 1,
        left: 1,
        zIndex: 1,
        maxWidth: "calc(100% - var(--spacing-2))",
        py: "0.5rem",
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    })
};

const imageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const contentStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 0.5
    }),
    large: css({
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 0.5
    })
};

const titleStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        overflow: "hidden",
        lineClamp: 2,
        lineHeight: "1.25"
    }),
    large: css({
        overflow: "hidden",
        lineClamp: 2,
        lineHeight: "1.6rem"
    })
};

const priceStyle = css({
    color: "var(--colors-secondary)",
    fontWeight: 700
});

const locationStyle = css({
    color: "var(--colors-gray-700)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const blurredPriceStyle = css({
    filter: "blur(4px)",
    userSelect: "none",
    pointerEvents: "none"
});
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {Currency} from "@pg-rp/common-types";

import {replaceDigitsWithZeros} from "../../../credit_calculator/utils/replace_digits_with_zeros";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {getCurrency} from "../../../utils/get_currency";
import {PropertyCarouselSize} from "../../types/PropertyCarouselSize";

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

interface IProps {
    href: string;
    imageSrc?: string;
    investmentName: string;
    rooms: number;
    area: number;
    floor: number | null;
    price: number | null;
    currency?: Currency;
    location: string;
    size?: PropertyCarouselSize;
    onClick?: () => void;
    dataTestId?: string;
}

export const PropertyCarouselTile = (props: IProps) => {
    const {href, imageSrc, investmentName, rooms, area, floor, price, currency, location, size = "small", onClick, dataTestId} = props;
    const {shouldHidePrices} = useShouldHidePrices();

    const hasPrice = price !== null && price > 0;
    const priceText = hasPrice ? priceFormat(price, {unit: getCurrency({currency})}) || null : null;
    const displayPrice = shouldHidePrices && priceText ? replaceDigitsWithZeros(priceText) : priceText;
    const floorLabel = getFloorLabel(floor);

    return (
        <li className={listItemStyle[size]} data-testid={dataTestId}>
            <article className={cardStyle}>
                <a
                    className={cardLinkStyle}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Zobacz ofertę: ${investmentName}`}
                    onClick={onClick}
                >
                    <div className={imageWrapperStyle[size]}>
                        <Badge variant="label_default" className={investmentBadgeStyle[size]}>
                            <Text variant="info_txt_2" as="span" strong>
                                {investmentName}
                            </Text>
                        </Badge>
                        <img className={imageStyle} src={imageSrc} alt={investmentName} loading="lazy" />
                    </div>

                    <div className={contentStyle[size]}>
                        <Text variant="info_txt_1" as="div" className={titleStyle[size]}>
                            {rooms} {roomsPluralize(rooms)}, {areaFormat(area)}
                            {floorLabel ? `, ${floorLabel}` : ""}
                        </Text>

                        {displayPrice && (
                            <Text
                                variant="info_txt_1"
                                as="div"
                                preserveFontSize={size === "large"}
                                className={cx(priceStyle, shouldHidePrices && blurredPriceStyle)}
                                aria-hidden={shouldHidePrices}
                            >
                                <Highlight strong>{displayPrice}</Highlight>
                            </Text>
                        )}

                        {location && (
                            <Text variant="info_txt_3" as="div" className={locationStyle}>
                                {location}
                            </Text>
                        )}
                    </div>
                </a>
            </article>
        </li>
    );
};

function getFloorLabel(floor: number | null): string {
    if (floor === null) {
        return "";
    }

    if (floor === 0) {
        return "parter";
    }

    return `${floor} piętro`;
}

const listItemStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        flex: "0 0 156px",
        scrollSnapAlign: "start",
        minWidth: "156px"
    }),
    large: css({
        flex: "0 0 192px",
        scrollSnapAlign: "start",
        minWidth: "192px"
    })
};

const cardStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "var(--colors-white)",
    borderRadius: 1,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden"
});

const cardLinkStyle = css({
    display: "flex",
    flexDirection: "column",
    color: "inherit",
    textDecoration: "none",
    minHeight: "100%",

    _hover: {
        color: "inherit",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "-2px"
    }
});

const imageWrapperStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        position: "relative",
        width: "100%",
        aspectRatio: "154/128",
        backgroundColor: "var(--colors-gray-100)",
        overflow: "hidden"
    }),
    large: css({
        position: "relative",
        width: "100%",
        aspectRatio: "192/157",
        backgroundColor: "var(--colors-gray-100)",
        overflow: "hidden"
    })
};

const investmentBadgeStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        position: "absolute",
        top: 0.5,
        left: 0.5,
        zIndex: 1,
        maxWidth: "calc(100% - var(--spacing-1))",
        py: 0.5,
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    }),
    large: css({
        position: "absolute",
        top: 1,
        left: 1,
        zIndex: 1,
        maxWidth: "calc(100% - var(--spacing-2))",
        py: "0.5rem",
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    })
};

const imageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const contentStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 0.5
    }),
    large: css({
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 0.5
    })
};

const titleStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        overflow: "hidden",
        lineClamp: 2,
        lineHeight: "1.25"
    }),
    large: css({
        overflow: "hidden",
        lineClamp: 2,
        lineHeight: "1.6rem"
    })
};

const priceStyle = css({
    color: "var(--colors-secondary)",
    fontWeight: 700
});

const locationStyle = css({
    color: "var(--colors-gray-700)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const blurredPriceStyle = css({
    filter: "blur(4px)",
    userSelect: "none",
    pointerEvents: "none"
});
